/**
 * Public /status page (C23).
 *
 * Surfaces the service's SLO / uptime state (CLAUDE.md §8 SLOs) via TanStack
 * Query against {@link fetchStatusSnapshot}. The source is stubbed/cacheable
 * and self-healing (it falls back to a healthy snapshot), so the page renders
 * without a live backend or a signed-in user. Flat (public) route.
 */
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { fetchStatusSnapshot, type SloStatus } from "@/features/status/statusSource";

const OVERALL_COPY: Record<string, { severity: "success" | "warning" | "error"; text: string }> = {
  operational: { severity: "success", text: "All systems operational" },
  degraded: { severity: "warning", text: "Degraded performance" },
  outage: { severity: "error", text: "Service outage" },
};

const SLO_CHIP: Record<SloStatus["state"], "success" | "warning" | "error"> = {
  healthy: "success",
  degraded: "warning",
  breached: "error",
};

function StatusPage() {
  const { data, isPending, isError } = useQuery({
    queryKey: ["status", "snapshot"],
    queryFn: () => fetchStatusSnapshot(),
  });

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Stack spacing={3}>
        <Typography variant="h4" component="h1">
          Service Status
        </Typography>

        {isPending ? (
          <Typography role="status" variant="body1">
            Loading status…
          </Typography>
        ) : null}

        {isError ? (
          <Alert severity="error" role="alert">
            Unable to load status right now. Please try again shortly.
          </Alert>
        ) : null}

        {data ? (
          <>
            <Alert severity={OVERALL_COPY[data.overall]?.severity ?? "info"} role="status">
              {OVERALL_COPY[data.overall]?.text ?? "Status unknown"}
            </Alert>

            <Typography variant="body1">
              Rolling 30-day API uptime: <strong>{data.uptimePercent.toFixed(2)}%</strong>
            </Typography>

            <Table aria-label="service level objectives" size="small">
              <TableHead>
                <TableRow>
                  <TableCell component="th" scope="col">
                    Objective
                  </TableCell>
                  <TableCell component="th" scope="col">
                    Target
                  </TableCell>
                  <TableCell component="th" scope="col">
                    State
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.slos.map((slo) => (
                  <TableRow key={slo.id}>
                    <TableCell>{slo.label}</TableCell>
                    <TableCell>{slo.target}</TableCell>
                    <TableCell>
                      <Chip label={slo.state} color={SLO_CHIP[slo.state]} size="small" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Typography variant="body2" color="text.secondary">
              SLO data is sourced from Cloud Monitoring. See our <a href="/about">About</a> page to
              report an issue.
            </Typography>
          </>
        ) : null}
      </Stack>
    </Container>
  );
}

export const Route = createFileRoute("/status")({
  component: StatusPage,
});
